/**
 * Global uncaught-error capture → Cloud Logging.
 *
 * React error boundaries only catch errors thrown during rendering. Uncaught
 * errors outside React (event handlers, timers, async) and unhandled promise
 * rejections previously reached neither pipeline — a real blind spot (see
 * ARCHITECTURE.md §14). This installs
 * `window.onerror` + `onunhandledrejection` handlers that forward to the same
 * `logger` the error boundaries use, so every uncaught failure lands in Cloud
 * Logging (queryable as `jsonPayload.source="frontend" AND level="error"`).
 *
 * Idempotent + SSR-safe. Install once at bootstrap.
 */

import { logger } from '../services/loggingService';

let installed = false;

const STACK_CAP = 1000; // keep entries under the backend's 4KB/log limit

function _stack(err: unknown): string | undefined {
  const s = (err as { stack?: string } | undefined)?.stack;
  return typeof s === 'string' ? s.slice(0, STACK_CAP) : undefined;
}

export function installGlobalErrorHandlers(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (event: ErrorEvent) => {
    logger.error('Uncaught error', {
      message: event.message,
      source: event.filename,
      line: event.lineno,
      column: event.colno,
      stack: _stack(event.error),
    });
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const reason = event.reason as { message?: string } | undefined;
    logger.error('Unhandled promise rejection', {
      message: reason?.message ?? String(event.reason),
      stack: _stack(event.reason),
    });
  });
}
